import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The input object to the CreateProduct class constructor.
 */
export interface CreateProductInput {
  /**
   * The name of the product
   */
  name: string;
  /**
   * A description used to sell the product.
   */
  description?: string;
  /**
   * The category identifier for the product used for grouping and display purposes.
   */
  category_id?: string;
  /**
   * An image used to sell a product, this should be a URL.
   */
  image?: string;
  /**
   * The payload added to the NFT during minting.
   */
  payload?: any;
  /**
   * Any additional JSON data to be stored against the product record for display purposes.
   */
  metadata?: any;
  /**
   * The price of the product in the currency denomination.
   */
  prices?: {
    price: number;
    currency: string;
  }[];
}

/**
 * The response expected from the CreateProduct execution within BambuClient.
 */
export interface CreateProductResponse {
  /**
   * The unique identifier for the BambuMeta product definition.
   */
  productId: string;
  /**
   * The name of the product
   */
  name: string;
  /**
   * A description used to sell the product.
   */
  description?: string;
  /**
   * The category identifier for the product used for grouping and display purposes.
   */
  category_id?: string;
  /**
   * An image used to sell a product, this should be a URL.
   */
  image?: string;
  /**
   * The payload added to the NFT during minting.
   */
  payload?: any;
  /**
   * Any additional JSON data to be stored against the product record for display purposes.
   */
  metadata?: any;
  /**
   * The price of the product in the currency denomination.
   */
  prices?: {
    price: number;
    currency: string;
  }[];
}

/**
 * Creates a product for the provided tenant to be able to generate and mint NFTs from.
 */
export class CreateProduct extends Executable<CreateProductResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the CreateProductResponse
   * @returns CreateProductResponse
   */
  public getResponse(apiResponse: any): CreateProductResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/products`;
  }

  public productId?: number;

  public name?: string;

  public description?: string;

  public image?: string;

  public category_id?: string;

  public payload?: any;

  public metadata?: any;

  public prices?: {
    price: number;
    currency: string;
  }[];

  constructor(input?: CreateProductInput) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * Sets the name of the product
   * @param value The name of the product
   * @returns <CreateProduct>
   */
  public setName(value: string): CreateProduct {
    this.name = value;
    return this;
  }

  /**
   * Sets a description used to sell the product.
   * @param value A description used to sell the product.
   * @returns <CreateProduct>
   */
  public setDescription(value: string): CreateProduct {
    this.description = value;
    return this;
  }

  /**
   * Set an image used to sell a product, this should be a URL.
   * @param value An image used to sell a product, this should be a URL.
   * @returns <CreateProduct>
   */
  public setImage(value: string): CreateProduct {
    this.image = value;
    return this;
  }

  /**
   * Sets the category identifier for the product used for grouping and display purposes.
   * @param value The category identifier for the product used for grouping and display purposes.
   * @returns <CreateProduct>
   */
  public setCategoryId(value: string): CreateProduct {
    this.category_id = value;
    return this;
  }

  /**
   * Any additional JSON data to be stored against the product record for display purposes.
   * @param key Sets the attribute's key
   * @param value Sets the attribute's value
   * @returns
   */
  public setMetaData(key: string, value: string): CreateProduct {
    if (!this.metadata) {
      this.metadata = {};
    }
    this.metadata[key] = value;
    return this;
  }

  /**
   * The price of the product in the currency denomination.
   * @param price The price of the product in the currency denomination.
   * @param currency The currency used for the provided item.
   */
  public addPrice(price: number, currency: string) {
    if (!this.prices) {
      this.prices = [];
    }
    this.prices?.push({
      currency,
      price,
    });
  }

  /**
   * Sets the payload added to the NFT during minting.
   * @param value The payload added to the NFT during minting.
   * @returns <CreateProduct>
   */
  public setPayload(payload: any): CreateProduct {
    this.payload = payload;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {};
    if (this.name) {
      input.name = this.name;
    }
    if (this.description) {
      input.description = this.description;
    }
    if (this.image) {
      input.image = this.image;
    }
    if (this.category_id) {
      input.category_id = this.category_id;
    }
    if (this.metadata) {
      input.metadata = this.metadata;
    }
    if (this.prices) {
      input.prices = this.prices;
    }
    if (this.payload) {
      input.payload = this.payload;
    }
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
